/**
 * 组织管理自定义Hook
 * 处理组织、部门、成员的数据加载和CRUD操作
 */

import { useState, useEffect, useCallback } from 'react';
import { message, FormInstance } from 'antd';
import {
    organizationAPI,
    departmentAPI,
    memberAPI,
    Organization,
    Department,
    Member,
    ModuleOption,
} from '../../../services/organizationAPI';

interface UseOrganizationManagementProps {
    deptForm?: FormInstance | null;
    memberForm?: FormInstance | null;
}

interface UseOrganizationManagementReturn {
    // 数据状态
    loading: boolean;
    organization: Organization | null;
    departments: Department[];
    members: Member[];
    modules: ModuleOption[];

    // 弹窗状态
    deptModalVisible: boolean;
    setDeptModalVisible: (visible: boolean) => void;
    editingDept: Department | null;
    memberModalVisible: boolean;
    setMemberModalVisible: (visible: boolean) => void;
    editingMember: Member | null;

    // 操作方法 - 部门
    handleAddDept: () => void;
    handleEditDept: (dept: Department) => void;
    handleSaveDept: () => Promise<void>;
    handleDeleteDept: (id: number) => Promise<void>;

    // 操作方法 - 成员
    handleAddMember: () => void;
    handleEditMember: (member: Member) => void;
    handleSaveMember: () => Promise<void>;
}

/**
 * 解析分页响应数据
 */
const extractArrayData = <T>(data: any): T[] => {
    if (Array.isArray(data)) return data;
    return data?.results || [];
};

export const useOrganizationManagement = ({
    deptForm,
    memberForm,
}: UseOrganizationManagementProps = {}): UseOrganizationManagementReturn => {
    // 数据状态
    const [loading, setLoading] = useState(false);
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [modules, setModules] = useState<ModuleOption[]>([]);

    // 弹窗状态
    const [deptModalVisible, setDeptModalVisible] = useState(false);
    const [editingDept, setEditingDept] = useState<Department | null>(null);
    const [memberModalVisible, setMemberModalVisible] = useState(false);
    const [editingMember, setEditingMember] = useState<Member | null>(null);

    // 加载数据
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [orgRes, deptRes, memberRes, moduleRes] = await Promise.all([
                organizationAPI.getCurrent(),
                departmentAPI.list(),
                memberAPI.list(),
                organizationAPI.getModules(),
            ]);
            setOrganization(orgRes.data);
            setDepartments(extractArrayData<Department>(deptRes.data));
            setMembers(extractArrayData<Member>(memberRes.data));
            setModules(extractArrayData<ModuleOption>(moduleRes.data));
        } catch (error: any) {
            if (error.response?.status !== 404) {
                message.error('加载数据失败');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // 初始化加载
    useEffect(() => {
        loadData();
    }, [loadData]);

    // ================== 部门管理 ==================
    const handleAddDept = useCallback(() => {
        setEditingDept(null);
        if (deptForm) {
            deptForm.resetFields();
        }
        setDeptModalVisible(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditDept = useCallback((dept: Department) => {
        setEditingDept(dept);
        if (deptForm) {
            deptForm.setFieldsValue({
                name: dept.name,
                description: dept.description,
                permissions: dept.permissions,
            });
        }
        setDeptModalVisible(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveDept = useCallback(async () => {
        if (!deptForm) return;
        try {
            const values = await deptForm.validateFields();
            if (editingDept) {
                await departmentAPI.update(editingDept.id, values);
                message.success('部门更新成功');
            } else {
                await departmentAPI.create(values);
                message.success('部门创建成功');
            }
            setDeptModalVisible(false);
            loadData();
        } catch (error: any) {
            message.error(error.response?.data?.detail || '保存失败');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingDept, loadData]);

    const handleDeleteDept = useCallback(async (id: number) => {
        try {
            await departmentAPI.delete(id);
            message.success('部门已删除');
            loadData();
        } catch (error) {
            message.error('删除失败');
        }
    }, [loadData]);

    // ================== 成员管理 ==================
    const handleAddMember = useCallback(() => {
        setEditingMember(null);
        if (memberForm) {
            memberForm.resetFields();
            memberForm.setFieldsValue({ role: 'member' });
        }
        setMemberModalVisible(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditMember = useCallback((member: Member) => {
        setEditingMember(member);
        if (memberForm) {
            memberForm.setFieldsValue({
                id: member.id,
                username: member.username,
                email: member.email,
                first_name: member.first_name,
                last_name: member.last_name,
                job_title: member.job_title,
                phone: member.phone,
                role: member.role,
                department: member.department,
                supervisor: member.supervisor,
            });
        }
        setMemberModalVisible(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveMember = useCallback(async () => {
        if (!memberForm) return;
        try {
            const values = await memberForm.validateFields();

            // department 为空字符串或 undefined 时要转为 null，或者 API 允许 null
            if (!values.department) {
                values.department = null;
            }

            if (editingMember) {
                // 编辑模式：调用 update
                await memberAPI.update(editingMember.id, values);
                message.success('成员信息已更新');
            } else {
                // 新增模式：调用 create
                await memberAPI.create(values);
                message.success('成员创建成功');
            }

            setMemberModalVisible(false);
            loadData();
        } catch (error: any) {
            console.error(error);
            const errorMsg = error.response?.data?.detail ||
                (error.response?.data && typeof error.response.data === 'object'
                    ? Object.values(error.response.data).join(', ')
                    : '保存失败');
            message.error(errorMsg);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingMember, loadData]);

    return {
        loading,
        organization,
        departments,
        members,
        modules,
        deptModalVisible,
        setDeptModalVisible,
        editingDept,
        memberModalVisible,
        setMemberModalVisible,
        editingMember,
        handleAddDept,
        handleEditDept,
        handleSaveDept,
        handleDeleteDept,
        handleAddMember,
        handleEditMember,
        handleSaveMember,
    };
};

export default useOrganizationManagement;
